/**
 * seoConfig.js  –  src/seoConfig.js
 *
 * Central SEO configuration for every route in AALI GROUP.
 * Keys must exactly match the <Route path="…"> values in App.js.
 *
 * Fields per page:
 *   title       – shown in browser tab & Google headline (≤ 60 chars)
 *   description – Google snippet / OG description (≤ 160 chars)
 *   canonical   – canonical path (leading slash, no domain)
 *   ogImage     – absolute URL for OG / Twitter card image
 *   ogType      – "website" (default) | "article" | "profile"
 *   schema      – JSON-LD object OR array of objects
 *   noindex     – true → robots: noindex, nofollow (omit from sitemap too)
 *
 * LOCAL SEO NOTE
 * ──────────────
 * Every title/description below is written around the Guntur / Andhra Pradesh
 * location intent ("top companies in guntur", "best business group in guntur",
 * …). Keep the NAP (Name, Address, Phone) below byte-for-byte identical to the
 * Google Business Profile and to the Footer/Contact page, otherwise Google will
 * not merge the signals and local rankings suffer.
 */

const BASE = 'https://aaligroup.co';
const LOGO = `${BASE}/assets/aali-logo.png`;
const OG   = `${BASE}/assets/aali-group-logo.png`;   // fallback OG image

// ── Canonical NAP – must match Footer.js, Contact.js and Google Business Profile
const LEGAL_NAME = 'AALI GROUP';
const PHONE      = '+91-83410-85415';   // also the WhatsApp line
const PHONE_2    = '+91-70758-93515';
const LANDLINE   = '+91-863-4611770';   // Guntur office landline
const EMAIL      = 'info@aaligroup.co';

/**
 * Spellings people actually type into Google (from Search Console queries:
 * "ali group", "aligroup", "ali group of companies"...).
 * `alternateName` lets Google match those misspellings to the real brand
 * without putting awkward variants into visible copy.
 */
const GROUP_ALT_NAMES = [
  'Aali Group', 'Aali Group of Companies', 'AALI GROUP Guntur',
  'Ali Group', 'Ali Group of Companies', 'Aligroup',
];

const CONTACT_POINTS = [
  {
    '@type':           'ContactPoint',
    telephone:          PHONE,
    email:              EMAIL,
    contactType:       'customer service',
    description:       'Phone and WhatsApp',
    areaServed:        'IN',
    availableLanguage: ['English', 'Telugu', 'Hindi'],
  },
  {
    '@type':           'ContactPoint',
    telephone:          PHONE_2,
    contactType:       'sales',
    areaServed:        'IN',
    availableLanguage: ['English', 'Telugu', 'Hindi'],
  },
  {
    '@type':           'ContactPoint',
    telephone:          LANDLINE,
    contactType:       'customer service',
    description:       'Office landline',
    areaServed:        'IN',
    availableLanguage: ['English', 'Telugu', 'Hindi'],
  },
];

const ADDRESS = {
  '@type':         'PostalAddress',
  streetAddress:   '4th Line, Syamala Nagar',
  addressLocality: 'Guntur',
  addressRegion:   'Andhra Pradesh',
  postalCode:      '522006',
  addressCountry:  'IN',
};

/**
 * Office coordinates: the AALI CONSSULTANCY Google Business Profile pin, in the
 * same building as every AALI company. Keep identical on all group websites
 * (only aalitech.co uses its own AALITECH pin).
 */
const GEO = {
  '@type':   'GeoCoordinates',
  latitude:   16.3071219,
  longitude:  80.4097439,
};

// Cities/areas the group actively serves — reinforces regional relevance.
const AREA_SERVED = [
  'Guntur', 'Vijayawada', 'Amaravati', 'Tenali', 'Narasaraopet',
  'Mangalagiri', 'Andhra Pradesh', 'India',
];

const OPENING_HOURS = [
  {
    '@type':     'OpeningHoursSpecification',
    dayOfWeek:  ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens:      '09:00',
    closes:     '19:00',
  },
];

// ▸ ACTION: add every live social profile here. Google uses `sameAs` to confirm
//   that the website, the Google Business Profile and the social pages are the
//   same real-world entity — one of the strongest local trust signals.
//   The Instagram/YouTube profiles that exist today belong to AALI CONSSULTANCY,
//   so they are listed on aaliconssultancy.com, not here.
const SAME_AS = [
  // 'https://www.linkedin.com/company/aali-group',
  // 'https://www.facebook.com/aaligroup',
  // 'https://www.youtube.com/@aaligroup',
];

const PARENT_ORG = {
  '@type': 'Organization',
  '@id':    `${BASE}/#organization`,
  name:    LEGAL_NAME,
  url:      BASE,
};

/**
 * Subsidiaries that have their own website. Their schema on this site points to
 * that website (url + @id) and uses that company's own phone and email, so Google
 * treats them as the same entity as the official site, not a second copy.
 */
const SUBSIDIARY_SITES = {
  aalitech:         { url: 'https://aalitech.co/',          telephone: '+91-70758-93515', email: 'info@aalitech.co' },
  aaliconssultancy: { url: 'https://aaliconssultancy.com/', telephone: '+91-70758-93515', email: 'info@aaliconssultancy.com' },
  aalibuilders:     { url: 'https://aalibuilders.com/',     telephone: '+91-83410-85415', email: 'info@aalibuilders.com' },
};

// Schema fields that link a subsidiary page here to the company's own website.
const officialSite = (slug) => {
  const { url, telephone, email } = SUBSIDIARY_SITES[slug];
  return { '@id': `${url}#organization`, url, sameAs: [url], telephone, email };
};

// ─────────────────────────────────────────────────────────────────────────────
// SCHEMAS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * BreadcrumbList builder.
 * Google shows these as the "aaligroup.co › Companies › AALITECH" trail in the
 * result, which raises click-through rate and clarifies site structure.
 *
 * @param {Array<[string, string]>} trail  – [label, path] pairs, Home excluded
 */
const breadcrumb = (trail) => ({
  '@context': 'https://schema.org',
  '@type':    'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: BASE },
    ...trail.map(([name, path], i) => ({
      '@type':   'ListItem',
      position:   i + 2,
      name,
      item:      `${BASE}${path}`,
    })),
  ],
});

/**
 * The head-office LocalBusiness node.
 * This is the single most important schema for "top companies in Guntur" style
 * searches — it is what makes the business eligible for the local map pack.
 */
const localBusinessSchema = {
  '@context':    'https://schema.org',
  '@type':       'LocalBusiness',
  '@id':         `${BASE}/#localbusiness`,
  name:           LEGAL_NAME,
  alternateName:  GROUP_ALT_NAMES,
  description:    'AALI GROUP is a diversified business group headquartered in Guntur, Andhra Pradesh, operating across technology, construction, media, luxury retail and social impact.',
  url:            BASE,
  logo:           LOGO,
  image:          OG,
  telephone:      PHONE,
  email:          EMAIL,
  contactPoint:   CONTACT_POINTS,
  address:        ADDRESS,
  geo:            GEO,
  hasMap:         'https://maps.app.goo.gl/H4Eb3U6G6xTJqs5s8',
  priceRange:     '₹₹',
  areaServed:     AREA_SERVED,
  openingHoursSpecification: OPENING_HOURS,
  currenciesAccepted: 'INR',
  parentOrganization: { '@id': `${BASE}/#organization` },
  ...(SAME_AS.length ? { sameAs: SAME_AS } : {}),
};

const homeSchema = [
  {
    '@context': 'https://schema.org',
    '@type':    'Organization',
    '@id':      `${BASE}/#organization`,
    name:        LEGAL_NAME,
    alternateName: GROUP_ALT_NAMES,
    url:          BASE,
    logo: {
      '@type':    'ImageObject',
      url:         LOGO,
      width:       400,
      height:      400,
    },
    description: 'AALI GROUP is a diversified conglomerate based in Guntur, Andhra Pradesh, committed to innovation, quality and social responsibility across technology, real estate, media, luxury and social impact.',
    address:      ADDRESS,
    telephone:    PHONE,
    email:        EMAIL,
    foundingDate: '2021',
    founder:     { '@type': 'Person', name: 'Azagar Ali Shaik' },
    areaServed:   AREA_SERVED,
    ...(SAME_AS.length ? { sameAs: SAME_AS } : {}),
    contactPoint: CONTACT_POINTS,
    knowsAbout: [
      'Software development', 'Web development', 'Mobile app development',
      'Digital marketing', 'Job placement', 'Education consultancy',
      'Construction', 'Real estate development', 'Film production',
      'Music production', 'Gold jewellery', 'Charitable trust',
    ],
    subOrganization: [
      { '@type': 'Organization', '@id': `${SUBSIDIARY_SITES.aalitech.url}#organization`,         name: 'AALITECH',          alternateName: ['Aalitech'], url: SUBSIDIARY_SITES.aalitech.url },
      { '@type': 'Organization', '@id': `${SUBSIDIARY_SITES.aaliconssultancy.url}#organization`, name: 'AALI CONSSULTANCY', alternateName: ['Aali Conssultancy'], url: SUBSIDIARY_SITES.aaliconssultancy.url },
      { '@type': 'Organization', '@id': `${SUBSIDIARY_SITES.aalibuilders.url}#organization`,     name: 'AALI BUILDERS',     url: SUBSIDIARY_SITES.aalibuilders.url },
      { '@type': 'Organization', name: 'AALI MOVIES',       url: `${BASE}/companies/aalimovies` },
      { '@type': 'Organization', name: 'AALI FILMZ',        url: `${BASE}/companies/aalifilmz` },
      { '@type': 'Organization', name: 'AALI MUSIC',        url: `${BASE}/companies/aalimusic` },
      { '@type': 'Organization', name: 'AALI GOLD JEWELZ',  url: `${BASE}/companies/aaligoldjewelz` },
      { '@type': 'Organization', name: 'AALI TRUSTZ',       url: `${BASE}/companies/aalitrustz` },
    ],
  },
  localBusinessSchema,
  {
    '@context':       'https://schema.org',
    '@type':          'WebSite',
    '@id':            `${BASE}/#website`,
    name:              LEGAL_NAME,
    url:               BASE,
    inLanguage:       'en-IN',
    publisher:        { '@id': `${BASE}/#organization` },
  },
  /**
   * FAQPage.
   *
   * NOTE: Google fully deprecated FAQ rich results on 7 May 2026 — this markup
   * will NOT produce the expandable FAQ block in Google Search any more. Google
   * has confirmed unused structured data causes no problems, so it is kept
   * because it is still read by Bing and by AI/LLM answer engines, and because
   * the matching visible FAQ on the page is genuinely useful content.
   *
   * The real ranking value is the VISIBLE FAQ in src/pages/Home.js, not this.
   * Keep the two in sync — Google's structured data policy forbids marking up
   * content that is not visible on the page.
   */
  {
    '@context': 'https://schema.org',
    '@type':    'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name:    'Which are the top business companies in Guntur?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:    'AALI GROUP is one of the leading diversified business groups in Guntur, Andhra Pradesh. It operates eight companies across software and digital marketing (AALITECH), job placement and education consultancy (AALI CONSSULTANCY), construction and real estate (AALI BUILDERS), film and media production (AALI MOVIES, AALI FILMZ), music (AALI MUSIC), gold jewellery retail (AALI GOLD JEWELZ) and charitable social work (AALI TRUSTZ).',
        },
      },
      {
        '@type': 'Question',
        name:    'Where is AALI GROUP located in Guntur?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:    'The AALI GROUP head office is at AALITECH, 4th Line, Syamala Nagar, Guntur, Andhra Pradesh 522006, India. Office hours are Monday to Saturday, 9:00 AM to 7:00 PM. You can call +91 83410 85415 or email info@aaligroup.co.',
        },
      },
      {
        '@type': 'Question',
        name:    'What businesses does AALI GROUP operate in Andhra Pradesh?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:    'AALI GROUP operates across five industries in Andhra Pradesh: technology and job consultancy, infrastructure and real estate development, media and entertainment, luxury and gold jewellery retail, and social impact through its charitable trust. The group serves Guntur, Vijayawada, Amaravati, Tenali, Mangalagiri and Narasaraopet.',
        },
      },
      {
        '@type': 'Question',
        name:    'Which AALI GROUP company offers software and IT services?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:    'AALITECH, the technology company of AALI GROUP, builds websites, mobile apps and business software and runs digital marketing (SEO, social media and PPC) for businesses in Guntur and across Andhra Pradesh. Its official website is aalitech.co.',
        },
      },
      {
        '@type': 'Question',
        name:    'Who is the founder of AALI GROUP?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:    'AALI GROUP was founded by Azagar Ali Shaik, who serves as Founder and Chairman. The group was established in 2021 and is headquartered in Guntur, Andhra Pradesh.',
        },
      },
      {
        '@type': 'Question',
        name:    'How can I contact AALI GROUP for business enquiries?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:    'You can reach AALI GROUP by phone at +91 83410 85415, by email at info@aaligroup.co, or through the contact form at aaligroup.co/contact. The head office in Syamala Nagar, Guntur is open Monday to Saturday.',
        },
      },
    ],
  },
];

const contactSchema = [
  {
    '@context': 'https://schema.org',
    '@type':    'ContactPage',
    name:        'Contact AALI GROUP – Guntur, Andhra Pradesh',
    url:         `${BASE}/contact`,
    publisher:  { '@id': `${BASE}/#organization` },
    mainEntity: { '@id': `${BASE}/#localbusiness` },
  },
  localBusinessSchema,
  breadcrumb([['Contact', '/contact']]),
];

const founderSchema = [
  {
    '@context': 'https://schema.org',
    '@type':    'ProfilePage',
    name:        'Azagar Ali Shaik – Founder & Chairman, AALI GROUP',
    url:         `${BASE}/founder`,
    mainEntity: {
      '@type':    'Person',
      name:      'Azagar Ali Shaik',
      jobTitle:  'Founder & Chairman',
      worksFor:  { '@id': `${BASE}/#organization` },
      address:    ADDRESS,
      image:     `${BASE}/assets/ceo.png`,
    },
  },
  breadcrumb([['Founder', '/founder']]),
];

const careersSchema = [
  {
    '@context': 'https://schema.org',
    '@type':    'WebPage',
    name:        'Careers & Job Openings at AALI GROUP, Guntur',
    url:         `${BASE}/careers`,
    publisher:  { '@id': `${BASE}/#organization` },
    description: 'Explore job openings and career opportunities across AALI GROUP companies in Guntur, Andhra Pradesh.',
  },
  breadcrumb([['Careers', '/careers']]),
];

const globalLocationsSchema = [
  {
    '@context': 'https://schema.org',
    '@type':    'WebPage',
    name:        'AALI GROUP – Office Locations',
    url:         `${BASE}/global-locations`,
    publisher:  { '@id': `${BASE}/#organization` },
    description: 'Offices and operations of AALI GROUP across Guntur, Andhra Pradesh, India and international markets.',
  },
  localBusinessSchema,
  breadcrumb([['Global Locations', '/global-locations']]),
];

/**
 * Company page schema builder.
 * `type` should be the most specific schema.org type available for the
 * business (JewelryStore, RealEstateAgent, …) — specific types are eligible
 * for richer local treatment than a generic Organization.
 */
const companySchema = (name, type, url, description, extraFields = {}) => ({
  '@context':          'https://schema.org',
  '@type':              type,
  name,
  url:                  `${BASE}${url}`,
  parentOrganization:   PARENT_ORG,
  description,
  address:              ADDRESS,
  telephone:            PHONE,
  email:                EMAIL,
  areaServed:           AREA_SERVED,
  ...extraFields,
});

// Service list → OfferCatalog (only services that are listed on the page).
const offerCatalog = (name, services) => ({
  '@type': 'OfferCatalog',
  name,
  itemListElement: services.map((service) => ({
    '@type':      'Offer',
    itemOffered: { '@type': 'Service', name: service, areaServed: AREA_SERVED },
  })),
});

// A local company page = company schema + its breadcrumb trail.
const companyPageSchema = (name, type, slug, description, extraFields = {}) => ([
  companySchema(name, type, `/companies/${slug}`, description, extraFields),
  breadcrumb([['Businesses', '/businesses'], [name, `/companies/${slug}`]]),
]);

// ─────────────────────────────────────────────────────────────────────────────
// PAGE MAP  (keys = exact route paths used in App.js)
// ─────────────────────────────────────────────────────────────────────────────

const seoPages = {

  /* ── MAIN ─────────────────────────────────────────────────────────────── */

  '/': {
    title:       'AALI GROUP | Top Companies in Guntur, Andhra Pradesh',
    description: 'AALI GROUP (Aali Group of Companies) — top business group in Guntur, AP: AALITECH software, job consultancy, builders, films, music, jewellery & charity trust.',
    canonical:   '/',
    ogImage:      OG,
    schema:       homeSchema,
  },

  '/about': {
    title:       'About Us | Leading Business Group in Guntur',
    description: "Learn about AALI GROUP — a leading diversified business group in Guntur, Andhra Pradesh. Our mission, values and journey across technology, real estate, media and retail.",
    canonical:   '/about',
    ogImage:      OG,
    schema:       breadcrumb([['About', '/about']]),
  },

  '/businesses': {
    title:       'Our Companies | Top Businesses in Guntur, AP',
    description: "Explore all 8 AALI GROUP companies in Guntur — IT services, construction, film production, music, gold jewellery and charitable trust. One group, many industries.",
    canonical:   '/businesses',
    ogImage:      OG,
    schema:       breadcrumb([['Businesses', '/businesses']]),
  },

  '/investors': {
    title:       'Investor Relations | AALI GROUP Guntur',
    description: 'AALI GROUP investor relations — financial performance, corporate governance and long-term growth strategy of a leading diversified group in Guntur, Andhra Pradesh.',
    canonical:   '/investors',
    ogImage:      OG,
    schema:       breadcrumb([['Investors', '/investors']]),
  },

  '/newsroom': {
    title:       'Newsroom | AALI GROUP News & Updates, Guntur',
    description: 'Latest news, press releases and announcements from AALI GROUP and its companies in Guntur, Andhra Pradesh — technology, real estate, media and luxury retail.',
    canonical:   '/newsroom',
    ogImage:      OG,
    schema:       breadcrumb([['Newsroom', '/newsroom']]),
  },

  '/careers': {
    title:       'Careers | Jobs & Openings in Guntur, AP',
    description: 'Job openings at AALI GROUP in Guntur — careers in IT, construction, media, retail and social impact. Join one of the fastest-growing business groups in Andhra Pradesh.',
    canonical:   '/careers',
    ogImage:      OG,
    schema:       careersSchema,
  },

  '/contact': {
    title:       'Contact Us | AALI GROUP Office in Guntur',
    description: 'Contact AALI GROUP, Syamala Nagar, Guntur. Call +91 70758 93515 or landline 0863 461 1770, WhatsApp +91 83410 85415, or email info@aaligroup.co.',
    canonical:   '/contact',
    ogImage:      OG,
    schema:       contactSchema,
  },

  '/founder': {
    title:       'Azagar Ali Shaik | Founder & Chairman, AALI GROUP',
    description: 'Meet Azagar Ali Shaik, Founder & Chairman of AALI GROUP — the entrepreneurial leadership behind one of the leading business groups in Guntur, Andhra Pradesh.',
    canonical:   '/founder',
    ogImage:     `${BASE}/assets/ceo.png`,
    ogType:      'profile',
    schema:       founderSchema,
  },

  '/global-locations': {
    title:       'Our Locations | AALI GROUP Offices & Presence',
    description: "AALI GROUP's presence — head office in Guntur, Andhra Pradesh with operations expanding across India and international markets.",
    canonical:   '/global-locations',
    ogImage:      OG,
    schema:       globalLocationsSchema,
  },

  /* ── INDUSTRIES ────────────────────────────────────────────────────────── */

  '/industries/tech-cons': {
    title:       'Technology & Consulting Companies | AALI GROUP',
    description: "AALI GROUP's technology and consulting companies: AALITECH (websites, apps and business software) and AALI CONSSULTANCY (jobs, admissions and study abroad).",
    canonical:   '/industries/tech-cons',
    ogImage:     `${BASE}/assets/aalitech_1.webp`,
    schema:       breadcrumb([['Industries', '/businesses'], ['Technology & Consulting', '/industries/tech-cons']]),
  },

  '/industries/builders-dev': {
    title:       'Infrastructure & Development | AALI GROUP',
    description: "AALI GROUP's infrastructure and development vertical, led by AALI BUILDERS, its construction and real estate company. Official website: aalibuilders.com",
    canonical:   '/industries/builders-dev',
    ogImage:     `${BASE}/assets/aali-infras.png`,
    schema:       breadcrumb([['Industries', '/businesses'], ['Builders & Development', '/industries/builders-dev']]),
  },

  '/industries/media-entertainment': {
    title:       'Media & Film Production Company in Guntur',
    description: "AALI GROUP's media and entertainment arm — film production, digital content and music from Guntur, Andhra Pradesh, under one creative banner.",
    canonical:   '/industries/media-entertainment',
    ogImage:     `${BASE}/assets/aali-movies.png`,
    schema:       breadcrumb([['Industries', '/businesses'], ['Media & Entertainment', '/industries/media-entertainment']]),
  },

  '/industries/luxury-retail': {
    title:       'Gold Jewellery & Luxury Retail in Guntur',
    description: "AALI GROUP's luxury and retail division — exquisite gold jewellery and premium products for customers in Guntur and across Andhra Pradesh.",
    canonical:   '/industries/luxury-retail',
    ogImage:     `${BASE}/assets/aali-gold-jewelz.png`,
    schema:       breadcrumb([['Industries', '/businesses'], ['Luxury & Retail', '/industries/luxury-retail']]),
  },

  '/industries/social-impact': {
    title:       'Social Impact & Charitable Trust in Guntur',
    description: "AALI GROUP's social impact initiatives in Guntur — community development, education, healthcare and charitable work through our trust and CSR programmes.",
    canonical:   '/industries/social-impact',
    ogImage:     `${BASE}/assets/aali-trustz.png`,
    schema:       breadcrumb([['Industries', '/businesses'], ['Social Impact', '/industries/social-impact']]),
  },

  /* ── COMPANIES ─────────────────────────────────────────────────────────── */

  '/companies/aalitech': {
    title:       'AALITECH — Part of AALI GROUP',
    description: 'AALITECH is the software and digital company of AALI GROUP in Guntur: websites, apps, business software and marketing. Official website: aalitech.co',
    canonical:   '/companies/aalitech',
    ogImage:     `${BASE}/assets/aalitech_1.webp`,
    schema:       companyPageSchema(
      'AALITECH', 'ProfessionalService', 'aalitech',
      'Software and IT company in Guntur offering website design and development, iOS and Android mobile app development, and digital marketing (SEO, social media, PPC).',
      {
        ...officialSite('aalitech'),
        alternateName: ['Aalitech', 'AALITECH Guntur'],
        geo: GEO,
        openingHoursSpecification: OPENING_HOURS,
        knowsAbout: ['Web development', 'Website design', 'Mobile app development', 'Android apps', 'iOS apps', 'Digital marketing', 'SEO', 'Social media marketing', 'PPC advertising'],
        hasOfferCatalog: offerCatalog('AALITECH services', [
          'Digital Marketing (SEO, Social Media, PPC)',
          'Web Development & Design',
          'Mobile App Development (iOS & Android)',
        ]),
      },
    ),
  },

  '/companies/aaliconssultancy': {
    title:       'AALI CONSSULTANCY — Part of AALI GROUP',
    description: 'AALI CONSSULTANCY is the education and employment consultancy of AALI GROUP in Guntur. Official website for services and contact: aaliconssultancy.com',
    canonical:   '/companies/aaliconssultancy',
    ogImage:     `${BASE}/assets/aali-consultancy.png`,
    schema:       companyPageSchema(
      'AALI CONSSULTANCY', 'EmploymentAgency', 'aaliconssultancy',
      'Job placement and education consultancy in Guntur — private sector jobs in India and overseas, overseas employment assistance and college admissions guidance.',
      {
        ...officialSite('aaliconssultancy'),
        alternateName: ['Aali Conssultancy', 'AALI CONSSULTANCY Guntur'],
        geo: GEO,
        openingHoursSpecification: OPENING_HOURS,
        hasOfferCatalog: offerCatalog('AALI CONSSULTANCY services', [
          'Private Sector Job Placements (India & Overseas)',
          'Overseas Employment Assistance',
          'Management Quota Admissions',
          'Admissions in India & International Institutions',
        ]),
      },
    ),
  },

  '/companies/aalibuilders': {
    title:       'AALI BUILDERS — Part of AALI GROUP',
    description: 'AALI BUILDERS is the construction and real estate company of AALI GROUP in Guntur. Official website for services and contact: aalibuilders.com',
    canonical:   '/companies/aalibuilders',
    ogImage:     `${BASE}/assets/aali-infras.png`,
    schema:       companyPageSchema(
      'AALI BUILDERS', 'GeneralContractor', 'aalibuilders',
      'Real estate development and construction company in Guntur delivering premium residential and commercial projects across Andhra Pradesh.',
      { ...officialSite('aalibuilders'), geo: GEO, openingHoursSpecification: OPENING_HOURS },
    ),
  },

  '/companies/aalimovies': {
    title:       'AALI MOVIES | Film Production House, Guntur',
    description: 'AALI MOVIES is the film production house of AALI GROUP, creating high-quality cinema and entertainment content from Guntur, Andhra Pradesh.',
    canonical:   '/companies/aalimovies',
    ogImage:     `${BASE}/assets/aali-movies.png`,
    schema:       companyPageSchema(
      'AALI MOVIES', 'EntertainmentBusiness', 'aalimovies',
      'Film production house creating cinema and entertainment content, based in Guntur, Andhra Pradesh.',
    ),
  },

  '/companies/aalifilmz': {
    title:       'AALI FILMZ | Digital Content Studio, Guntur',
    description: 'AALI FILMZ is a creative film production and digital content studio in Guntur, Andhra Pradesh — telling stories that inspire across screens and platforms.',
    canonical:   '/companies/aalifilmz',
    ogImage:     `${BASE}/assets/aali-filmz.png`,
    schema:       companyPageSchema(
      'AALI FILMZ', 'EntertainmentBusiness', 'aalifilmz',
      'Creative film production and digital content studio based in Guntur, Andhra Pradesh.',
    ),
  },

  '/companies/aalimusic': {
    title:       'AALI MUSIC | Music Label & Studio in Guntur',
    description: 'AALI MUSIC is the music production label of AALI GROUP in Guntur, Andhra Pradesh — nurturing talent and creating memorable musical experiences.',
    canonical:   '/companies/aalimusic',
    ogImage:     `${BASE}/assets/music.png`,
    schema:       companyPageSchema(
      'AALI MUSIC', 'MusicGroup', 'aalimusic',
      'Music production label and audio studio based in Guntur, Andhra Pradesh.',
    ),
  },

  '/companies/aaligoldjewelz': {
    title:       'AALI GOLD JEWELZ | Gold Jewellery in Guntur',
    description: 'AALI GOLD JEWELZ offers exquisite gold jewellery and luxury accessories in Guntur, Andhra Pradesh — craftsmanship and elegance in every piece.',
    canonical:   '/companies/aaligoldjewelz',
    ogImage:     `${BASE}/assets/aali-gold-jewelz.png`,
    schema:       companyPageSchema(
      'AALI GOLD JEWELZ', 'JewelryStore', 'aaligoldjewelz',
      'Gold jewellery and luxury accessories showroom in Guntur, Andhra Pradesh.',
      { geo: GEO, openingHoursSpecification: OPENING_HOURS },
    ),
  },

  '/companies/aalitrustz': {
    title:       'AALI TRUSTZ | Charitable Trust & NGO in Guntur',
    description: 'AALI TRUSTZ is the charitable foundation of AALI GROUP, working on education, healthcare and community welfare in Guntur, Andhra Pradesh.',
    canonical:   '/companies/aalitrustz',
    ogImage:     `${BASE}/assets/aali-trustz.png`,
    schema:       companyPageSchema(
      'AALI TRUSTZ', 'NGO', 'aalitrustz',
      'Charitable foundation in Guntur working on education, healthcare and community welfare across Andhra Pradesh.',
      { geo: GEO },
    ),
  },

  /* ── UTILITY (noindex) ─────────────────────────────────────────────────── */

  '/coming-soon': {
    title:       'Coming Soon',
    description: 'Something exciting is on the way from AALI GROUP. Stay tuned.',
    canonical:   '/coming-soon',
    noindex:      true,
  },
};

export default seoPages;
